import {
	OrderQuotePurpose,
	useCreateOrderQuoteMutation,
	useSetQuoteClientContactsMutation,
} from '@codezo/graphql';
import { useState } from 'react';
import { toast } from 'sonner';
import { AddClientModal } from '../../../pages/clients/AddClientModal';
import { getMutationError } from '../../../lib/mutation-response';
import { AdminUserSelector } from '../../selectors/AdminUserSelector';
import { ClientContactMultiSelector } from '../../selectors/ClientContactMultiSelector';
import { OrganizationSelector } from '../../selectors/OrganizationSelector';
import { VetterMultiSelector } from '../../selectors/VetterMultiSelector';
import { EMPTY_ADDRESS, type AddressData } from '../../ui/address-form';
import { AddressPicker } from '../../ui/address-picker';
import { Button } from '../../ui/button';
import { CollapsibleSection } from '../../ui/collapsible-section';
import { DatePicker } from '../../ui/date-picker';
import { DialogBody, DialogFooter, DialogForm } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { SelectPopover } from '../../ui/select-popover';
import { Textarea } from '../../ui/textarea';

/* ── Purpose options ── */

export const PURPOSE_OPTIONS = [
	{ value: OrderQuotePurpose.NotSpecified, label: 'Not specified' },
	{ value: OrderQuotePurpose.Packaging, label: 'Packaging' },
	{ value: OrderQuotePurpose.Event, label: 'Event' },
	{ value: OrderQuotePurpose.Resell, label: 'Resell' },
	{ value: OrderQuotePurpose.Other, label: 'Other' },
];

export interface CreatedQuote {
	id: string;
	name: string;
}

interface CreateOrderQuoteFormProps {
	/** Whether the surrounding dialog is open — gates selector queries. */
	open: boolean;
	/** Fired after the quote is created, with the new quote's id + name. */
	onCreated: (quote: CreatedQuote) => void;
	onCancel: () => void;
	submitLabel?: string;
	cancelLabel?: string;
	/** Lock the client to a specific organization (e.g. when adding org-scoped products). */
	lockedOrganizationId?: string;
	/** Display name for the locked organization. */
	lockedOrganizationName?: string;
}

/**
 * The full "new quote" form, shared between the standalone CreateOrderQuoteModal
 * and the inline create step inside AddToQuoteModal. Renders its own dialog body
 * + footer so consumers only supply the DialogContent/Header shell.
 */
export function CreateOrderQuoteForm({
	open,
	onCreated,
	onCancel,
	submitLabel = 'Create quote',
	cancelLabel = 'Cancel',
	lockedOrganizationId,
	lockedOrganizationName,
}: CreateOrderQuoteFormProps) {
	const [name, setName] = useState('');
	const [organizationId, setOrganizationIdState] = useState(lockedOrganizationId ?? '');
	const [clientContactUserIds, setClientContactUserIds] = useState<string[]>([]);
	// Contacts belong to one client, so a different client clears them.
	const setOrganizationId = (id: string) => {
		setOrganizationIdState(id);
		if (id !== organizationId) setClientContactUserIds([]);
	};
	const [description, setDescription] = useState('');
	const [purpose, setPurpose] = useState('');
	const [purposeOther, setPurposeOther] = useState('');
	const [deadline, setDeadline] = useState<Date | null>(null);
	const [adminUserIds, setAdminUserIds] = useState<string[]>([]);
	const [vetterEntityIds, setVetterEntityIds] = useState<string[]>([]);
	const [address, setAddress] = useState<AddressData>(EMPTY_ADDRESS);

	// Inline "create client" flow — opened from the client selector dropdown.
	// The created client is preselected once the AddClientModal succeeds.
	const [addClientOpen, setAddClientOpen] = useState(false);
	const [addClientSeed, setAddClientSeed] = useState('');
	const [createdClient, setCreatedClient] = useState<{ id: string; name: string } | null>(null);

	const [createOrderQuote, { loading: creating }] = useCreateOrderQuoteMutation();
	const [setClientContacts, { loading: savingContacts }] = useSetQuoteClientContactsMutation();
	const loading = creating || savingContacts;

	/**
	 * `createOrderQuote` takes no contacts, so they're set straight after. The
	 * quote exists by then whatever happens here — a failure is a warning that
	 * points at the quote page, not a failed create.
	 */
	const saveClientContacts = async (orderQuoteId: string) => {
		if (clientContactUserIds.length === 0) return;
		try {
			const { data } = await setClientContacts({
				variables: { input: { orderQuoteId, organizationUserIds: clientContactUserIds } },
			});
			const error = getMutationError(data?.setQuoteClientContacts);
			if (error) {
				toast.warning('Client contacts weren’t saved', {
					description: `${error} Everyone at the client will be emailed until you choose them on the quote.`,
				});
			}
		} catch {
			toast.warning('Client contacts weren’t saved', {
				description:
					'Everyone at the client will be emailed until you choose them on the quote.',
			});
		}
	};

	const handleSubmit = async () => {
		if (!name.trim() || !organizationId) return;

		try {
			const { data } = await createOrderQuote({
				variables: {
					input: {
						name: name.trim(),
						organizationId,
						description: description.trim() || undefined,
						purpose: purpose ? (purpose as OrderQuotePurpose) : undefined,
						// Only carries meaning under OTHER — every other purpose names
						// itself, so a leftover from a changed answer would describe the
						// wrong one.
						purposeOther:
							purpose === OrderQuotePurpose.Other && purposeOther.trim()
								? purposeOther.trim()
								: undefined,
						deadline: deadline?.toISOString() || undefined,
						adminUserIds: adminUserIds.length > 0 ? adminUserIds : undefined,
						vetterEntityIds: vetterEntityIds.length > 0 ? vetterEntityIds : undefined,
						deliveryAddress: address.addressLine1
							? {
									addressLine1: address.addressLine1,
									addressLine2: address.addressLine2 || undefined,
									city: address.city || undefined,
									stateCode: address.stateCode || undefined,
									postalCode: address.postalCode || undefined,
									countryCode: address.countryCode || undefined,
									googlePlaceId: address.googlePlaceId || undefined,
									latitude: address.latitude,
									longitude: address.longitude,
								}
							: undefined,
					},
				},
			});

			const created = data?.createOrderQuote;
			if (created?.success && created.referenceKey) {
				await saveClientContacts(created.referenceKey);
				toast.success('Quote created');
				onCreated({ id: created.referenceKey, name: name.trim() });
			} else if (created?.success) {
				// Created but no id returned — can't continue into it.
				toast.error('Quote created but could not be opened');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create quote');
		}
	};

	const canSubmit = !!name.trim() && !!organizationId;

	const detailsParts = [
		purpose === OrderQuotePurpose.Other && purposeOther.trim()
			? purposeOther.trim()
			: purpose && PURPOSE_OPTIONS.find((o) => o.value === purpose)?.label,
		adminUserIds.length > 0 &&
			`${adminUserIds.length} admin${adminUserIds.length > 1 ? 's' : ''}`,
		vetterEntityIds.length > 0 &&
			`${vetterEntityIds.length} vetter${vetterEntityIds.length > 1 ? 's' : ''}`,
	].filter(Boolean);
	const detailsHint = detailsParts.length > 0 ? detailsParts.join(' · ') : undefined;

	return (
		<>
			<DialogForm onSubmit={handleSubmit}>
				<DialogBody>
					<div className="flex flex-col gap-4">
						{/* ── Required fields (always visible) ── */}
						<div className="flex flex-col gap-1.5">
							<Label>
								Name <span className="text-destructive">*</span>
							</Label>
							<Input
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder="e.g. Spring Collection 2026"
							/>
						</div>

						<div className="flex flex-col gap-1.5">
							<Label>
								Client <span className="text-destructive">*</span>
							</Label>
							<OrganizationSelector
								key={createdClient?.id ?? 'organization'}
								value={organizationId}
								onChange={setOrganizationId}
								placeholder="Search clients…"
								skip={!open}
								disabled={!!lockedOrganizationId}
								selectedLabel={
									lockedOrganizationName ??
									(createdClient && organizationId === createdClient.id
										? createdClient.name
										: undefined)
								}
								onCreateNew={
									lockedOrganizationId
										? undefined
										: (query) => {
												setAddClientSeed(query);
												setAddClientOpen(true);
											}
								}
							/>
							{lockedOrganizationId && (
								<div className="text-xs text-text-4">
									Locked to the selected products’ organization.
								</div>
							)}
						</div>

						{/* Only once there's a client — contacts belong to it. */}
						{organizationId && (
							<div className="flex flex-col gap-1.5">
								<Label>Client contacts</Label>
								<ClientContactMultiSelector
									organizationId={organizationId}
									value={clientContactUserIds}
									onChange={setClientContactUserIds}
									skip={!open}
								/>
								<div className="text-xs text-text-4">
									Who gets emails about this quote. Leave empty to email everyone;
									guest-link holders are always emailed.
								</div>
							</div>
						)}

						<div className="flex flex-col gap-1.5">
							<Label>Description</Label>
							<Textarea
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								placeholder="Brief description of this quote"
								rows={2}
							/>
						</div>

						{/* ── Details (collapsible) ── */}
						<CollapsibleSection label="Details" hint={detailsHint}>
							<div className="flex flex-col gap-4">
								<div className="grid grid-cols-2 gap-3">
									<div className="flex flex-col gap-1.5">
										<Label>Purpose</Label>
										<SelectPopover
											value={purpose}
											onChange={(next) => {
												setPurpose(next);
												// Dropped rather than kept hidden: the payload
												// is built from state, so a stale answer would
												// save against a purpose that no longer asks
												// the question.
												if (next !== OrderQuotePurpose.Other)
													setPurposeOther('');
											}}
											options={PURPOSE_OPTIONS}
											placeholder="Select purpose"
										/>
									</div>
									<div className="flex flex-col gap-1.5">
										<Label>Deadline</Label>
										<DatePicker
											value={deadline}
											onChange={(d) => setDeadline(d ?? null)}
											placeholder="Select date"
											minDate={new Date()}
										/>
									</div>
								</div>

								{purpose === OrderQuotePurpose.Other && (
									<div className="flex flex-col gap-1.5">
										<Label>What is it for?</Label>
										<Input
											value={purposeOther}
											onChange={(e) => setPurposeOther(e.target.value)}
											placeholder="Describe the purpose"
										/>
									</div>
								)}

								<div className="flex flex-col gap-1.5">
									<Label>Assigned admins</Label>
									<AdminUserSelector
										value={adminUserIds}
										onChange={setAdminUserIds}
										skip={!open}
									/>
								</div>

								<div className="flex flex-col gap-1.5">
									<Label>Vetters</Label>
									<VetterMultiSelector
										value={vetterEntityIds}
										onChange={setVetterEntityIds}
										skip={!open}
									/>
								</div>
							</div>
						</CollapsibleSection>

						{/* ── Delivery address (collapsible) ──
						    Defaults to the client's primary address once one is
						    picked above, so the common case is already answered. */}
						<CollapsibleSection
							label="Delivery address"
							hint={address.addressLine1 ? address.addressLine1 : undefined}
						>
							{organizationId ? (
								<AddressPicker
									organizationId={organizationId}
									value={address}
									onChange={setAddress}
									skip={!open}
								/>
							) : (
								<div className="rounded-lg border border-dashed border-line-2 px-3 py-4 text-center text-sm text-text-4">
									Choose a client first — their saved addresses go here.
								</div>
							)}
						</CollapsibleSection>
					</div>
				</DialogBody>

				<DialogFooter>
					<Button type="button" variant="outline" onClick={onCancel}>
						{cancelLabel}
					</Button>
					<Button type="submit" disabled={!canSubmit || loading}>
						{loading ? 'Creating…' : submitLabel}
					</Button>
				</DialogFooter>
			</DialogForm>

			{/* Nested "create client" dialog — created client gets preselected above. */}
			<AddClientModal
				open={addClientOpen}
				onOpenChange={setAddClientOpen}
				initialName={addClientSeed}
				onSuccess={(id, clientName) => {
					if (id) {
						setOrganizationId(id);
						setCreatedClient({ id, name: clientName || 'New client' });
					}
				}}
			/>
		</>
	);
}
